import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useParams } from "wouter";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft,
  Loader2,
  AlertCircle,
  Send,
  HelpCircle,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import InvoiceFormCard, {
  invoiceFormSchema,
  type InvoiceFormValues,
  formatMoneyFieldValue,
} from "@/components/invoicing/InvoiceFormCard";
import { useInvoice, useUpdateInvoice, InvoiceLockedByClaimError } from "@/hooks/useInvoices";
import MoveInvoiceDialog from "@/components/invoicing/MoveInvoiceDialog";
import { CommissionInquiryDialog } from "@/components/invoicing/CommissionInquiryDialog";
import { FINANCES_PATHS } from "@/lib/financesPaths";
import { useAuth } from "@/context/AuthContext";
import { useImpersonation } from "@/context/ImpersonationContext";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { isInvoiceTramsSyncError } from "@shared/schema";
import {
  formatTramsInvoiceDisplayNumber,
  getTramsInvoiceNumberFromMetadata,
} from "@shared/invoice-display";


function toFormDate(val: string | Date | null | undefined): string {
  if (!val) return "";
  const str = typeof val === "string" ? val : val.toISOString();
  const datePart = str.substring(0, 10);
  const d = new Date(datePart + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  return datePart;
}

function invoiceToFormValues(inv: any): InvoiceFormValues {
  // Support both new fields and legacy fallback (split primaryTraveler on /)
  let firstName = inv.travelerFirstName ?? "";
  let lastName = inv.travelerLastName ?? "";
  if (!firstName && !lastName && inv.primaryTraveler) {
    const parts = (inv.primaryTraveler as string).split("/");
    lastName = parts[0] ?? "";
    firstName = parts.slice(1).join("/") ?? "";
  }
  return {
    travelerFirstName: firstName,
    travelerLastName: lastName,
    supplierLinkNo: inv.supplierLinkNo ?? undefined,
    supplierName: inv.supplierName ?? "",
    travelType: inv.travelType ?? "",
    propertyName: inv.propertyName ?? "",
    shipName: inv.shipName ?? "",
    cabinCount: inv.cabinCount ?? undefined,
    tourName: inv.tourName ?? "",
    description: inv.description ?? "",
    confirmationNumber: inv.confirmationNumber ?? "",
    departureDate: toFormDate(inv.departureDate),
    returnDate: toFormDate(inv.returnDate),
    totalFare: inv.totalFare != null ? formatMoneyFieldValue(inv.totalFare) : "",
    commissionAmount: inv.commissionAmount != null ? formatMoneyFieldValue(inv.commissionAmount) : "",
    agencyLinkNo: inv.agencyLinkNo ?? 0,
  };
}

export default function EditInvoice() {
  const params = useParams<{ id: string }>();
  const invoiceId = params?.id ? parseInt(params.id, 10) : undefined;
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { impersonation } = useImpersonation();
  const operationIdRef = useRef(crypto.randomUUID());
  const updateInvoice = useUpdateInvoice();

  const { data: invoice, isLoading, error } = useInvoice(invoiceId);

  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [initialValues, setInitialValues] = useState<InvoiceFormValues | null>(null);

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: initialValues ?? undefined,
    mode: "onBlur",
  });

  useEffect(() => {
    if (invoice && !initialValues) {
      const vals = invoiceToFormValues(invoice);
      setInitialValues(vals);
      form.reset(vals);
    }
  }, [invoice, initialValues, form]);

  const isCreator = useMemo(() => {
    if (!invoice) return false;
    const effectiveUserId = impersonation.active && impersonation.targetUserId
      ? impersonation.targetUserId
      : user?.id;
    if (!effectiveUserId) return false;
    return invoice.createdBy === effectiveUserId;
  }, [user, invoice, impersonation]);

  const isOpen = invoice?.invoiceStatusCode === "O";
  const isPendingVoid = invoice?.status === "voided" && isOpen;
  // `lockedByClaim` is populated when an active unclaimed-commission
  // claim references this invoice. Server enforces the same lock; this
  // guard just keeps the user from typing into a form that won't save.
  const lockedByClaim = invoice?.lockedByClaim ?? null;
  const canEdit = isOpen && isCreator && !isPendingVoid && !lockedByClaim;

  const hasChanges = (): boolean => {
    if (!initialValues) return false;
    const current = form.getValues();
    return JSON.stringify(current) !== JSON.stringify(initialValues);
  };

  const { requestLeave } = useUnsavedChangesGuard(hasChanges);

  const handleCancel = () => {
    requestLeave(() => setLocation(FINANCES_PATHS.invoices));
  };

  const handleSubmit = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();
    if (!invoiceId) return;

    operationIdRef.current = crypto.randomUUID();

    try {
      const updated = await updateInvoice.mutateAsync({
        id: invoiceId,
        data: {
          ...values,
          departureDate: values.departureDate ? new Date(values.departureDate).toISOString() : undefined,
          returnDate: values.returnDate ? new Date(values.returnDate).toISOString() : undefined,
        } as any,
        operationId: operationIdRef.current,
      });

      setInitialValues(invoiceToFormValues(updated));
      form.reset(invoiceToFormValues(updated));

      toast({
        title: "Invoice Updated",
        description: "Changes saved. Sync status: Pending.",
      });
      setLocation(FINANCES_PATHS.invoices);
    } catch (err: any) {
      // Race window: if a claim landed between page load and Save, the
      // server now rejects with INVOICE_LOCKED_BY_UNCLAIMED_CLAIM. Surface
      // a tailored toast and bounce the user back to the list.
      if (err instanceof InvoiceLockedByClaimError) {
        toast({
          title: "Invoice now locked",
          description: `An unclaimed-commission claim (#${err.claimId ?? "?"}) was submitted against this invoice. It is now under Worldvia support review and cannot be edited.`,
          variant: "destructive",
        });
        setLocation(FINANCES_PATHS.invoices);
        return;
      }
      toast({
        title: "Update Failed",
        description: err.message || "Failed to update invoice.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-lg font-medium">Invoice not found</p>
        <Button variant="outline" onClick={() => setLocation(FINANCES_PATHS.invoices)}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  if (!canEdit) {
    const reason = lockedByClaim
      ? `This invoice is linked to unclaimed-commission claim #${lockedByClaim.claimId} (status: ${lockedByClaim.status}) and is under Worldvia support review. Edits are disabled until the claim is resolved. Contact support to make changes.`
      : isPendingVoid
      ? "This invoice cannot be edited while a void request is in progress."
      : !isOpen
        ? `This invoice is ${invoice.invoiceStatusCode === "C" ? "Closed" : "Voided"} and cannot be edited.`
        : "Only the invoice creator can edit this invoice.";

    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-lg font-medium">
          {lockedByClaim ? "Invoice Locked — Under Support Review" : "Cannot Edit Invoice"}
        </p>
        <p className="text-muted-foreground text-sm max-w-md text-center">{reason}</p>
        <Button variant="outline" onClick={() => setLocation(FINANCES_PATHS.invoices)}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  const syncErrorBadge = isInvoiceTramsSyncError(invoice.syncStatus)
    ? {
        label: "TRAMS Sync Failed",
        variant: "destructive" as const,
        icon: <AlertCircle className="h-3 w-3" />,
      }
    : null;

  return (
    <div className="flex flex-col h-full">
      {/* Top Bar */}
      <div className="flex-shrink-0 border-b bg-background px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex flex-col gap-3 max-w-4xl mx-auto sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold">
                Edit Invoice #{formatTramsInvoiceDisplayNumber(getTramsInvoiceNumberFromMetadata(invoice.metadata))}
              </h1>
              {syncErrorBadge && (
                <Badge variant={syncErrorBadge.variant} className="gap-1">
                  {syncErrorBadge.icon}
                  {syncErrorBadge.label}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowInquiryDialog(true)}
              className="text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700"
            >
              <HelpCircle className="h-4 w-4 mr-1" />
              Commission Inquiry
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowMoveDialog(true)}
              disabled={
                invoice.invoiceStatusCode !== "O" ||
                invoice.status === "voided" ||
                updateInvoice.isPending
              }
              title={
                invoice.invoiceStatusCode !== "O"
                  ? "Only Open invoices can be moved"
                  : "Move this invoice to a different trip"
              }
            >
              <Send className="h-4 w-4 mr-1" />
              Move to Trip…
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={updateInvoice.isPending}
            >
              {updateInvoice.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </div>
      </div>

      {isInvoiceTramsSyncError(invoice.syncStatus) && (
        <div className="max-w-4xl mx-auto w-full px-6 pt-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              TRAMS rejected this invoice during sync. Review the details, save
              corrections to retry, or contact support if the issue persists.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Form */}
      <ScrollArea className="flex-1">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <FormProvider {...form}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <InvoiceFormCard
                form={form}
                index={0}
                tramsInvoiceNumber={getTramsInvoiceNumberFromMetadata(invoice.metadata)}
                canRemove={false}
              />
            </form>
          </FormProvider>
        </div>
      </ScrollArea>

      <MoveInvoiceDialog
        open={showMoveDialog}
        onOpenChange={setShowMoveDialog}
        invoice={{
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          metadata: invoice.metadata,
          tripId: invoice.tripId,
          recordLocator: invoice.recordLocator,
          invoiceStatusCode: invoice.invoiceStatusCode,
          status: invoice.status,
        }}
        onMoved={() => {
          setLocation(FINANCES_PATHS.invoices);
        }}
      />

      <CommissionInquiryDialog
        open={showInquiryDialog}
        onOpenChange={setShowInquiryDialog}
        invoiceId={invoice?.id ?? null}
      />
    </div>
  );
}
